import { useState, useEffect, useMemo } from "react";
import { CalendarDate } from "@internationalized/date";
import type { DateValue } from "react-aria-components";
import { useListData } from "react-stately";
import { Input } from "@/components/base/input/input";
import { Select } from "@/components/base/select/select";
import { MultiSelect } from "@/components/base/select/multi-select";
import type { SelectItemType } from "@/components/base/select/select";
import { DateRangePicker } from "@/components/application/date-picker/date-range-picker";
import type { ActivityFilters } from "@/types/activity";

interface Props {
    value: ActivityFilters;
    onChange: (next: ActivityFilters) => void;
}

// Convert Date to CalendarDate
const dateToCalendarDate = (date: Date): CalendarDate => {
    return new CalendarDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
};

// Convert CalendarDate to Date
const calendarDateToDate = (dateValue: DateValue): Date => {
    return new Date(dateValue.year, dateValue.month - 1, dateValue.day);
};

// Activity category options for multi-select filter
// 6 active categories: document, tracking, issue, email, contact, agent
const categoryOptions: SelectItemType[] = [
    { id: "document", label: "Documenti" },
    { id: "tracking", label: "Tracking" },
    { id: "issue", label: "Issue" },
    { id: "email", label: "Email" },
    { id: "contact", label: "Rubrica" },
    { id: "agent", label: "Sistema" },
];

export const ActivityFiltersBar = ({ value, onChange }: Props) => {
    const [local, setLocal] = useState<ActivityFilters>(value);

    // Initialize selected categories from value prop
    const selectedCategories = useListData<SelectItemType>({
        initialItems: value.activityCategories
            ? categoryOptions.filter((opt) => value.activityCategories?.includes(opt.id as string))
            : [],
        getKey: (item) => item.id,
    });

    useEffect(() => setLocal(value), [value]);

    // Sync category selection changes with parent
    useEffect(() => {
        const categoryIds = selectedCategories.items.map((item) => item.id as string);
        const currentCategories = local.activityCategories || [];

        // Only update if actually changed
        if (JSON.stringify(categoryIds.sort()) !== JSON.stringify([...currentCategories].sort())) {
            const next = {
                ...local,
                activityCategories: categoryIds.length > 0 ? categoryIds : undefined,
            };
            setLocal(next);
            onChange(next);
        }
    }, [selectedCategories.items]);

    const readOptions = [
        { id: "all", label: "Tutte" },
        { id: "unread", label: "Non lette" },
        { id: "read", label: "Lette" },
    ];

    const dateRangeValue = useMemo(() => {
        if (local.fromDate && local.toDate) {
            return {
                start: dateToCalendarDate(local.fromDate),
                end: dateToCalendarDate(local.toDate),
            };
        }
        return null;
    }, [local.fromDate, local.toDate]);

    return (
        <div className="rounded-xl bg-primary p-4 shadow-xs ring-1 ring-secondary">
            <div className="flex flex-wrap gap-3">
                {/* Stato lettura - fixed width */}
                <div className="w-40">
                    <Select
                        placeholder="Stato lettura"
                        items={readOptions}
                        selectedKey={(local.readState || "all") as string}
                        onSelectionChange={(key) => {
                            const next = { ...local, readState: key as any };
                            setLocal(next);
                            onChange(next);
                        }}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>
                </div>

                {/* Categoria - wider to accommodate multiple selections, no max width to use all space */}
                <div className="min-w-80 flex-[2]">
                    <MultiSelect
                        placeholder="Filtra per categoria..."
                        items={categoryOptions}
                        selectedItems={selectedCategories}
                        size="md"
                        placeholderIcon={null}
                    >
                        {(item) => (
                            <MultiSelect.Item key={item.id} id={item.id}>
                                {item.label}
                            </MultiSelect.Item>
                        )}
                    </MultiSelect>
                </div>

                {/* Date range */}
                <div className="w-64">
                    <DateRangePicker
                        value={dateRangeValue}
                        onChange={(range) => {
                            const next: ActivityFilters = {
                                ...local,
                                fromDate: range?.start ? calendarDateToDate(range.start) : null,
                                toDate: range?.end ? calendarDateToDate(range.end) : null,
                            };
                            setLocal(next);
                            onChange(next);
                        }}
                    />
                </div>

                {/* Keyword search - takes remaining space */}
                <div className="min-w-48 flex-1">
                    <Input
                        placeholder="Cerca per keyword..."
                        value={local.keyword || ""}
                        onChange={(v) => {
                            const next = { ...local, keyword: v };
                            setLocal(next);
                            onChange(next);
                        }}
                        size="md"
                    />
                </div>
            </div>
        </div>
    );
};

export default ActivityFiltersBar;
